import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook for managing URL parameters with state synchronization
 * @param {Object} defaultParams - Default parameter values
 * @param {Object} options - Configuration options
 * @param {boolean} options.replace - Whether to replace history entry instead of pushing new one
 * @param {Function} options.serialize - Custom serialization function for complex values
 * @param {Function} options.deserialize - Custom deserialization function for complex values
 * @returns {Array} [params, setParams, updateParam, clearParams]
 */
const useURLParams = (defaultParams = {}, options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    replace = true,
    serialize = JSON.stringify,
    deserialize = JSON.parse
  } = options;

  // Parse URL parameters on mount and location change
  const parseURLParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = { ...defaultParams };

    // Parse each parameter from URL
    for (const [key, value] of searchParams.entries()) {
      if (value) {
        try {
          // Try to parse as JSON first (for complex objects)
          if (value.startsWith('{') || value.startsWith('[')) {
            params[key] = deserialize(value);
          } else if (value === 'true') {
            params[key] = true;
          } else if (value === 'false') {
            params[key] = false;
          } else {
            // Keep all other values as strings (including numbers like tab IDs)
            params[key] = value;
          }
        } catch {
          // If JSON parsing fails, use as string
          params[key] = value;
        }
      }
    }

    return params;
  }, [location.search, defaultParams, deserialize]);

  const [params, setParamsState] = useState(parseURLParams);

  // Update state when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setParamsState(parseURLParams());
  }, [parseURLParams]);

  // Update URL when parameters change
  const updateURL = useCallback((newParams) => {
    const searchParams = new URLSearchParams();

    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'object') {
          searchParams.set(key, serialize(value));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    const newSearch = searchParams.toString();
    const newURL = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;

    navigate(newURL, { replace });
  }, [navigate, location.pathname, replace, serialize]);

  // Set multiple parameters at once
  const setParams = useCallback((newParams) => {
    if (typeof newParams === 'function') {
      const updatedParams = newParams(params);
      setParamsState(updatedParams);
      updateURL(updatedParams);
    } else {
      const updatedParams = { ...params, ...newParams };
      setParamsState(updatedParams);
      updateURL(updatedParams);
    }
  }, [params, updateURL]);

  // Update a single parameter
  const updateParam = useCallback((key, value) => {
    setParamsState(prevParams => {
      const updatedParams = { ...prevParams, [key]: value };
      updateURL(updatedParams); // Call updateURL with the truly updated params
      return updatedParams;
    });
  }, [updateURL]); // params removed from dependency array as it's now accessed via functional update

  // Clear all parameters (reset to defaults)
  const clearParams = useCallback(() => {
    setParamsState(defaultParams);
    updateURL(defaultParams);
  }, [defaultParams, updateURL]);

  // Get a specific parameter value
  const getParam = useCallback((key, fallback = null) => {
    return params[key] !== undefined ? params[key] : fallback;
  }, [params]);

  return {
    params,
    setParams,
    updateParam,
    clearParams,
    getParam
  };
};

export default useURLParams;
